

export const frontMatter = {};
export const contentTitle = '@deck.gl/mapbox';
export {default as metadata} from '@site/.docusaurus/docusaurus-plugin-content-docs/default/site-docs-api-reference-mapbox-overview-md-f4d.json'
export const assets = {

};

import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
export const toc = [{
  "value": "Installation",
  "id": "installation",
  "level": 2
}, {
  "value": "Include the Standalone Bundle",
  "id": "include-the-standalone-bundle",
  "level": 3
}, {
  "value": "Install from NPM",
  "id": "install-from-npm",
  "level": 3
}, {
  "value": "Camera Synchronization between deck.gl and Mapbox",
  "id": "camera-synchronization-between-deckgl-and-mapbox",
  "level": 3
}, {
  "value": "Using mapbox-gl/maplibre-gl controls and plugins with deck.gl",
  "id": "using-mapbox-glmaplibre-gl-controls-and-plugins-with-deckgl",
  "level": 3
}, {
  "value": "Mixing deck.gl layers and Map layers",
  "id": "mixing-deckgl-layers-and-map-layers",
  "level": 3
}, {
  "value": "Interleaved Renderer Compatibility",
  "id": "interleaved-renderer-compatibility",
  "level": 4
}, {
  "value": "Alternative Mapbox Integrations",
  "id": "alternative-mapbox-integrations",
  "level": 2
}, {
  "value": "Limitations",
  "id": "limitations",
  "level": 2
}];
function _createMdxContent(props) {
  const _components = {
    a: "a",
    blockquote: "blockquote",
    code: "code",
    h1: "h1",
    h2: "h2",
    h3: "h3",
    h4: "h4",
    header: "header",
    li: "li",
    p: "p",
    pre: "pre",
    table: "table",
    tbody: "tbody",
    td: "td",
    th: "th",
    thead: "thead",
    tr: "tr",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  };
  return _jsxs(_Fragment, {
    children: [_jsx(_components.header, {
      children: _jsx(_components.h1, {
        id: "deckglmapbox",
        children: "@deck.gl/mapbox"
      })
    }), "\n", _jsx(_components.p, {
      children: "This module integrates deck.gl into the Mapbox GL JS API-compatible ecosystem."
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: ["It synchronizes a deck.gl ", _jsx(_components.code, {
          children: "MapView"
        }), " with the mapbox-gl/maplibre-gl camera."]
      }), "\n", _jsxs(_components.li, {
        children: ["It allows deck.gl to be used with mapbox-gl/maplibre-gl ", _jsx(_components.a, {
          href: "https://docs.mapbox.com/mapbox-gl-js/api/markers",
          children: "controls"
        }), " and ", _jsx(_components.a, {
          href: "https://docs.mapbox.com/mapbox-gl-js/plugins/",
          children: "plugins"
        }), " such as ", _jsx(_components.code, {
          children: "NavigationControl"
        }), ", ", _jsx(_components.code, {
          children: "GeolocateControl"
        }), " and ", _jsx(_components.code, {
          children: "mapbox-gl-geocoder"
        }), "."]
      }), "\n", _jsx(_components.li, {
        children: "It adds the option to interleave deck.gl layers with the base map layers, such as drawing behind map labels, z-occlusion between deck.gl 3D objects and buildings, etc."
      }), "\n"]
    }), "\n", _jsxs(_components.p, {
      children: ["This module may be used in the React, Pure JS, and Scripting Environments. Visit the ", _jsx(_components.a, {
        href: "/docs/developer-guide/base-maps/using-with-mapbox",
        children: "mapbox base map developer guide"
      }), " for examples of each option."]
    }), "\n", _jsxs(_components.p, {
      children: ["When you use this module, Mapbox/Maplibre is the root HTML element and deck.gl is the child, with the map library handling all user inputs. Some of deck.gl's features are therefore unavailable due to limitations of the map library's API, see ", _jsx(_components.a, {
        href: "#limitations",
        children: "limitations"
      }), "."]
    }), "\n", _jsx(_components.p, {
      children: "It may be easier to understand the concepts of the module if you are already a mapbox-gl/maplibre-gl developer."
    }), "\n", _jsx("img", {
      src: "https://raw.github.com/visgl/deck.gl-data/master/images/whats-new/mapbox-layers.jpg"
    }), "\n", _jsx(_components.h2, {
      id: "installation",
      children: "Installation"
    }), "\n", _jsx(_components.h3, {
      id: "include-the-standalone-bundle",
      children: "Include the Standalone Bundle"
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-html",
        children: "<script src='https://api.tiles.mapbox.com/mapbox-gl-js/v3.2.0/mapbox-gl.js'></script>\n<!-- or -->\n<script src='https://unpkg.com/maplibre-gl@5.0.0/dist/maplibre-gl.js'></script>\n<script src=\"https://unpkg.com/deck.gl@^9.0.0/dist.min.js\"></script>\n<script type=\"text/javascript\">\n  const {MapboxOverlay} = deck;\n</script>\n"
      })
    }), "\n", _jsx(_components.h3, {
      id: "install-from-npm",
      children: "Install from NPM"
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-bash",
        children: "npm install @deck.gl/mapbox\n"
      })
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-js",
        children: "import {MapboxOverlay} from '@deck.gl/mapbox';\n"
      })
    }), "\n", _jsx(_components.h3, {
      id: "camera-synchronization-between-deckgl-and-mapbox",
      children: "Camera Synchronization between deck.gl and Mapbox"
    }), "\n", _jsxs(_components.p, {
      children: ["This module keeps a deck.gl ", _jsx(_components.code, {
        children: "MapView"
      }), " or ", _jsx(_components.code, {
        children: "GlobeView"
      }), " in sync with the mapbox-gl/maplibre-gl camera so that the base map and deck layers are always geospactially aligned. Some ", _jsx(_components.code, {
        children: "Deck"
      }), " props, such as ", _jsx(_components.code, {
        children: "viewState"
      }), ", are ignored or have different behavior. See ", _jsx(_components.code, {
        children: "MapboxOverlay"
      }), " constructor notes. Also, some camera features are unable to be fully synchronized due to API limitations, see ", _jsx(_components.a, {
        href: "#limitations",
        children: "limitations"
      }), "."]
    }), "\n", _jsx(_components.h3, {
      id: "using-mapbox-glmaplibre-gl-controls-and-plugins-with-deckgl",
      children: "Using mapbox-gl/maplibre-gl controls and plugins with deck.gl"
    }), "\n", _jsxs(_components.p, {
      children: ["The Mapbox ecosystem offers many well-designed controls, from the basic functionalities of ", _jsx(_components.code, {
        children: "NavigationControl"
      }), ", ", _jsx(_components.code, {
        children: "Popup"
      }), " and ", _jsx(_components.code, {
        children: "GeolocateControl"
      }), ", to vendor-service-bound UI implementations such as ", _jsx(_components.code, {
        children: "mapbox-gl-geocoder"
      }), " and ", _jsx(_components.code, {
        children: "mapbox-gl-directions"
      }), ". These libraries require that the Mapbox Map holds the source of truth of the camera state, instead of the normal ", _jsx(_components.a, {
        href: "/docs/developer-guide/interactivity",
        children: "state management"
      }), " by ", _jsx(_components.code, {
        children: "Deck"
      }), ". When you use the ", _jsx(_components.code, {
        children: "MapboxOverlay"
      }), " classes from this module, deck.gl plays nice with all the mapbox-gl peripherals."]
    }), "\n", _jsx(_components.h3, {
      id: "mixing-deckgl-layers-and-map-layers",
      children: "Mixing deck.gl layers and Map layers"
    }), "\n", _jsx(_components.p, {
      children: "Some important information in the base map could be hidden by a deck.gl visualization layer, and controlling opacity is not enough. A typical example of this is labels and roads, where it is desirable to have a deck.gl visualization layer render on top of the Mapbox geography, but where one might still want to see e.g. labels and/or roads. Alternatively, the deck.gl visualization should cover the ground, but not the roads and labels."
    }), "\n", _jsxs(_components.p, {
      children: ["To inject a deck layer into the map layers stack add an ", _jsx(_components.code, {
        children: "interleaved: true"
      }), " props to the ", _jsx(_components.a, {
        href: "/docs/api-reference/mapbox/mapbox-overlay",
        children: "MapboxOverlay"
      }), " control and add a ", _jsx(_components.code, {
        children: "beforeId"
      }), " prop to any layer passed to the ", _jsx(_components.a, {
        href: "/docs/api-reference/mapbox/mapbox-overlay",
        children: "MapboxOverlay"
      }), " control."]
    }), "\n", _jsxs(_components.p, {
      children: ["Mapbox provides an example of ", _jsx(_components.a, {
        href: "https://www.mapbox.com/mapbox-gl-js/example/geojson-layer-in-stack/",
        children: "finding the first label layer"
      }), ". For more sophisticated injection point lookups, refer to Mapbox' documentation on the format of Mapbox style layers, see ", _jsx(_components.a, {
        href: "https://www.mapbox.com/mapbox-gl-js/style-spec/#layers",
        children: "Mapbox Style Spec"
      }), "."]
    }), "\n", _jsxs(_components.p, {
      children: ["In some cases, the application wants to add a deck.gl 3D layer (e.g. ArcLayer, HexagonLayer, GeoJsonLayer) on top of a Mapbox basemap, while seamlessly blend into the z-buffer. This will interleave the useful visualization layers from both the deck.gl and Mapbox layer catalogs. In this case, a ", _jsx(_components.code, {
        children: "beforeId"
      }), " is not needed."]
    }), "\n", _jsx(_components.h4, {
      id: "interleaved-renderer-compatibility",
      children: "Interleaved Renderer Compatibility"
    }), "\n", _jsxs(_components.p, {
      children: ["The following table details renderer support across different versions of mapbox-gl and maplibre-gl. See ", _jsx(_components.a, {
        href: "/docs/get-started/using-with-map#base-maps-renderers",
        children: "base map renderers"
      }), " to learn about the differences between overlaid and interleaved renderers."]
    }), "\n", _jsxs(_components.table, {
      children: [_jsx(_components.thead, {
        children: _jsxs(_components.tr, {
          children: [_jsx(_components.th, {
            children: "Library"
          }), _jsx(_components.th, {
            children: "Overlaid (default)"
          }), _jsx(_components.th, {
            children: "Interleaved"
          })]
        })
      }), _jsxs(_components.tbody, {
        children: [_jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "mapbox-gl-js (before v2.13)"
          }), _jsx(_components.td, {
            children: "✓"
          }), _jsx(_components.td, {})]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "mapbox-gl-js v2.13+"
          }), _jsx(_components.td, {
            children: "✓"
          }), _jsxs(_components.td, {
            children: ["✓ with ", _jsx(_components.code, {
              children: "useWebGL2: true"
            })]
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "mapbox-gl-js v3+"
          }), _jsx(_components.td, {
            children: "✓"
          }), _jsx(_components.td, {
            children: "✓"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "maplibre-gl-js (before v3)"
          }), _jsx(_components.td, {
            children: "✓"
          }), _jsx(_components.td, {})]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "maplibre-gl-js v3+"
          }), _jsx(_components.td, {
            children: "✓"
          }), _jsx(_components.td, {
            children: "✓*"
          })]
        })]
      })]
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsx(_components.p, {
        children: "*will fallback to WebGL1 if WebGL2 is not available"
      }), "\n"]
    }), "\n", _jsx(_components.h2, {
      id: "alternative-mapbox-integrations",
      children: "Alternative Mapbox Integrations"
    }), "\n", _jsxs(_components.p, {
      children: ["If you're using deck.gl in a React or Scripting environment, you just want the base map as a back drop, and do not need mapbox-gl's UI controls or need to mix deck.gl and Mapbox layers, it is recommended that you do not use this module and instead use deck.gl as the root HTML element. Visit ", _jsx(_components.a, {
        href: "/docs/developer-guide/base-maps/using-with-mapbox#reverse-controlled",
        children: "Using Deck.gl as the root HTML element"
      }), " for an example."]
    }), "\n", _jsx(_components.h2, {
      id: "limitations",
      children: "Limitations"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: ["When using deck.gl's multi-view system, only one of the views can match the base map and receive interaction. See ", _jsx(_components.a, {
          href: "/docs/api-reference/mapbox/mapbox-overlay#multi-view-usage",
          children: "using MapboxOverlay with multi-views"
        }), " for details."]
      }), "\n", _jsxs(_components.li, {
        children: ["When using deck.gl as Mapbox layers or controls, ", _jsx(_components.code, {
          children: "Deck"
        }), " only receives a subset of user inputs delegated by ", _jsx(_components.code, {
          children: "Map"
        }), ". Therefore, certain interactive callbacks like ", _jsx(_components.code, {
          children: "onDrag"
        }), ", ", _jsx(_components.code, {
          children: "onInteractionStateChange"
        }), " are not available."]
      }), "\n", _jsx(_components.li, {
        children: "Mapbox/MapLibre's terrain features are partially supported. When a terrain is used, the camera of deck.gl and the base map should synchronize, however the deck.gl data with z=0 are rendered at the sea level and not aligned with the terrain surface."
      }), "\n", _jsx(_components.li, {
        children: "Mapbox's non-Mercator projections are not supported as their API doesn't expose the parameters used. Maplibre's globe projection is fully supported."
      }), "\n", _jsxs(_components.li, {
        children: ["The ", _jsx(_components.code, {
          children: "position"
        }), " property in ", _jsx(_components.code, {
          children: "viewState"
        }), " has no equivalent in mapbox-gl."]
      }), "\n"]
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}

